import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Eye, Mail, Printer, Edit, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Quote, Customer } from "@/types/crm";
import QuoteForm from "./QuoteForm";
import CustomerQuoteForm from "./CustomerQuoteForm";
import QuoteView from "./QuoteView";

interface QuoteManagementProps {
  quotes: Quote[];
  customers: Customer[];
  onRefresh: () => void;
}

const QuoteManagement = ({ quotes, customers, onRefresh }: QuoteManagementProps) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [viewingQuote, setViewingQuote] = useState<Quote | null>(null);
  const [deletingQuote, setDeletingQuote] = useState<Quote | null>(null);
  const [emailingQuote, setEmailingQuote] = useState<Quote | null>(null);
  const [emailAddress, setEmailAddress] = useState("");
  const [isEmailSending, setIsEmailSending] = useState(false);
  const { toast } = useToast();

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, "default" | "destructive" | "outline" | "secondary"> = {
      draft: "secondary",
      sent: "default",
      accepted: "secondary",
      rejected: "destructive",
      expired: "outline"
    };
    return <Badge variant={statusColors[status] || "default"}>{status}</Badge>;
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || "Unknown Customer";
  };

  const handleViewQuote = (quote: Quote) => {
    setViewingQuote(quote);
  };

  const handlePrintQuote = (quote: Quote) => {
    // For now, just open the quote view which can be printed
    setViewingQuote(quote);
    // Add a slight delay to allow the dialog to open, then trigger print
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const handleEmailQuote = async (quote: Quote) => {
    if (!emailAddress.trim()) {
      toast({
        title: "Error", 
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    setIsEmailSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-quote-email', {
        body: {
          quoteId: quote.id,
          recipientEmail: emailAddress.trim(),
          recipientName: emailAddress.split('@')[0]
        }
      });

      if (error) {
        console.error("Function invocation error:", error);
        toast({
          title: "Error",
          description: "Failed to send quote email.",
          variant: "destructive",
        });
      } else {
        console.log("Email sent successfully:", data);
        toast({
          title: "Success",
          description: `Quote email sent successfully to ${emailAddress}!`,
        });
        setEmailingQuote(null);
        setEmailAddress("");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while sending the email.",
        variant: "destructive",
      });
    } finally {
      setIsEmailSending(false);
    }
  };

  const openEmailDialog = (quote: Quote) => {
    const customer = customers.find(c => c.id === quote.customer_id);
    setEmailingQuote(quote);
    setEmailAddress(customer?.email || "");
  };

  const handleEditQuote = async (quote: Quote) => {
    try {
      // Fetch line items for this quote
      const { data: lineItems, error } = await supabase
        .from('quote_line_items')
        .select('*')
        .eq('quote_id', quote.id)
        .order('created_at');

      if (error) throw error;

      // Add line items to the quote object
      const quoteWithLineItems = {
        ...quote,
        line_items: lineItems || []
      };

      setEditingQuote(quoteWithLineItems);
    } catch (error) {
      console.error('Error fetching quote line items:', error);
      toast({
        title: "Error",
        description: "Failed to load quote details",
        variant: "destructive",
      });
    }
  };

  const handleDeleteQuote = async (quote: Quote) => {
    try {
      // First delete the line items
      const { error: lineItemsError } = await supabase
        .from('quote_line_items')
        .delete()
        .eq('quote_id', quote.id);

      if (lineItemsError) throw lineItemsError;

      // Then delete the quote
      const { error: quoteError } = await supabase
        .from('quotes')
        .delete()
        .eq('id', quote.id);

      if (quoteError) throw quoteError;

      toast({
        title: "Success",
        description: "Quote deleted successfully",
      });

      setDeletingQuote(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast({
        title: "Error",
        description: "Failed to delete quote",
        variant: "destructive",
      });
    }
  };

  if (showCreateForm) {
    return (
      <QuoteForm
        customers={customers}
        onQuoteCreated={() => {
          setShowCreateForm(false);
          onRefresh();
        }}
        onCancel={() => setShowCreateForm(false)}
      />
    );
  }

  if (showCustomerForm) {
    return (
      <CustomerQuoteForm
        customers={customers}
        onQuoteCreated={() => {
          setShowCustomerForm(false);
          onRefresh();
        }}
        onCancel={() => setShowCustomerForm(false)}
      />
    );
  }

  if (editingQuote) {
    return (
      <QuoteForm
        customers={customers}
        editingQuote={editingQuote}
        onQuoteCreated={() => {
          setEditingQuote(null);
          onRefresh();
        }}
        onCancel={() => setEditingQuote(null)}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Quote Management</CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowCreateForm(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Standard Quote
            </Button>
            <Button 
              className="bg-primary hover:bg-primary/90"
              onClick={() => setShowCustomerForm(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Customer Quote
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {quotes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No quotes created yet.</p>
            <p className="text-sm mt-2">Create your first quote to get started.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quote #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Total Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((quote) => (
                <TableRow key={quote.id}>
                  <TableCell className="font-medium">{quote.quote_number}</TableCell>
                  <TableCell>{getCustomerName(quote.customer_id)}</TableCell>
                  <TableCell>{quote.title}</TableCell>
                  <TableCell>${quote.total_amount.toFixed(2)}</TableCell>
                  <TableCell>{getStatusBadge(quote.status)}</TableCell>
                  <TableCell>{new Date(quote.created_at).toLocaleDateString()}</TableCell>
                   <TableCell>
                     <div className="flex gap-2">
                       <Button 
                         size="sm" 
                         variant="outline"
                         onClick={() => handleViewQuote(quote)}
                         title="View Quote"
                       >
                         <Eye className="h-4 w-4" />
                       </Button>
                       <Button 
                         size="sm" 
                         variant="outline"
                         onClick={() => handleEditQuote(quote)}
                         title="Edit Quote"
                       >
                         <Edit className="h-4 w-4" />
                       </Button>
                       <Button 
                         size="sm" 
                         variant="outline"
                         onClick={() => openEmailDialog(quote)}
                         title="Email Quote"
                       >
                         <Mail className="h-4 w-4" />
                       </Button>
                       <Button 
                         size="sm" 
                         variant="outline"
                         onClick={() => handlePrintQuote(quote)}
                         title="Print Quote"
                       >
                         <Printer className="h-4 w-4" />
                       </Button>
                       <Button 
                         size="sm" 
                         variant="destructive"
                         onClick={() => setDeletingQuote(quote)}
                         title="Delete Quote"
                       >
                         <Trash2 className="h-4 w-4" />
                       </Button>
                     </div>
                   </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        
        {/* Quote View Dialog */}
        <Dialog open={!!viewingQuote} onOpenChange={() => setViewingQuote(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Quote Details</DialogTitle>
            </DialogHeader>
            {viewingQuote && (
              <QuoteView 
                quote={viewingQuote} 
                customer={customers.find(c => c.id === viewingQuote.customer_id)} 
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Email Quote Dialog */}
        <Dialog open={!!emailingQuote} onOpenChange={() => {
          setEmailingQuote(null);
          setEmailAddress("");
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Email Quote {emailingQuote?.quote_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Email Address
                </label>
                <input
                  id="emailAddress"
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter email address"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => emailingQuote && handleEmailQuote(emailingQuote)}
                  disabled={isEmailSending || !emailAddress.trim()}
                  className="bg-yellow-800 hover:bg-yellow-900"
                >
                  {isEmailSending ? (
                    <>
                      <Mail className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send Email
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEmailingQuote(null);
                    setEmailAddress("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingQuote} onOpenChange={() => setDeletingQuote(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Quote</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete quote {deletingQuote?.quote_number}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => deletingQuote && handleDeleteQuote(deletingQuote)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default QuoteManagement;
